import { useState, useEffect } from "react";
// @mui
import {
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import { LoadingButton } from '@mui/lab';
// hooks
import useResponsive from "../../hooks/useResponsive";
import WeekdayTimeRangeSelector from './WeekdayTimeRangeSelector';

export default function CoreTimeDialog({ isOpen, closeDialog, initialCoreTime, setCoreTime, coreTimeUpdated=false, submittingCoreTime=false }) {
  const smUp = useResponsive('up', 'sm');
  // 요일별 ranges state (여러 구간)
  const [monRanges, setMonRanges] = useState([]);
  const [tueRanges, setTueRanges] = useState([]);
  const [wedRanges, setWedRanges] = useState([]);
  const [thuRanges, setThuRanges] = useState([]);
  const [friRanges, setFriRanges] = useState([]);
  const [satRanges, setSatRanges] = useState([]);
  const [sunRanges, setSunRanges] = useState([]);

  // 초기값 저장용 state
  const [initialRanges, setInitialRanges] = useState({
    월: [],
    화: [],
    수: [],
    목: [],
    금: [],
    토: [],
    일: [],
  });

  const weekdayLabelList = ['월','화','수','목','금','토','일'];
  const allRanges = {
    월: monRanges,
    화: tueRanges,
    수: wedRanges,
    목: thuRanges,
    금: friRanges,
    토: satRanges,
    일: sunRanges,
  };
  const setAllRanges = (newAll) => {
    setMonRanges(newAll['월']);
    setTueRanges(newAll['화']);
    setWedRanges(newAll['수']);
    setThuRanges(newAll['목']);
    setFriRanges(newAll['금']);
    setSatRanges(newAll['토']);
    setSunRanges(newAll['일']);
  };

  // ranges 배열 비교 함수
  const areRangesEqual = (ranges1, ranges2) => {
    // null/undefined 체크
    if (!ranges1 || !ranges2) {
      return false;
    }
    
    // 길이 비교
    if (ranges1.length !== ranges2.length) {
      return false;
    }
    
    // 빈 배열인 경우
    if (ranges1.length === 0 && ranges2.length === 0) {
      return true;
    }
    
    // 정렬 후 비교 (더 안전한 정렬)
    const sorted1 = [...ranges1].sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start;
      return a.end - b.end;
    });
    const sorted2 = [...ranges2].sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start;
      return a.end - b.end;
    });
    
    // 각 범위 비교
    return sorted1.every((range1, index) => {
      const range2 = sorted2[index];
      return range1.start === range2.start && range1.end === range2.end;
    });
  };

  // 초기값과 현재값 비교
  const hasChanges = () => {
    if (!coreTimeUpdated) {
      return true; // 새로 등록하는 경우는 항상 true
    }
    
    return !weekdayLabelList.every(day => 
      areRangesEqual(allRanges[day], initialRanges[day])
    );
  };

  // initialCoreTime -> ranges 변환 (초기값 적용)
  useEffect(() => {
    if (isOpen && initialCoreTime && Array.isArray(initialCoreTime)) {
      // 기존 값들 초기화
      const newRanges = {
        월: [],
        화: [],
        수: [],
        목: [],
        금: [],
        토: [],
        일: [],
      };

      initialCoreTime.forEach((time) => {
        // start, end 값 추출 (객체 또는 JSON 문자열 처리)
        let startValue = time.start;
        let endValue = time.end;
        
        // 객체인 경우 .value 직접 접근
        if (typeof startValue === 'object' && startValue !== null && startValue.value) {
          startValue = startValue.value;
        }
        // JSON 문자열인 경우 파싱 후 .value 접근
        else if (typeof startValue === 'string' && startValue.trim().startsWith('{')) {
          try {
            startValue = JSON.parse(startValue).value;
          } catch (e) {}
        }
        
        // 객체인 경우 .value 직접 접근
        if (typeof endValue === 'object' && endValue !== null && endValue.value) {
          endValue = endValue.value;
        }
        // JSON 문자열인 경우 파싱 후 .value 접근
        else if (typeof endValue === 'string' && endValue.trim().startsWith('{')) {
          try {
            endValue = JSON.parse(endValue).value;
          } catch (e) {}
        }
        
        const toIdx = (t) => {
          const [h, m] = t.split(':').map(Number);
          return h * 2 + (m === 30 ? 1 : 0);
        };
        const range = { start: toIdx(startValue), end: toIdx(endValue) - 1 };
        
        const dayMap = ['월', '화', '수', '목', '금', '토', '일'];
        const dayName = dayMap[time.weekday];
        if (dayName) {
          newRanges[dayName].push(range);
        }
      });

      // 초기값 저장 (깊은 복사)
      const deepCopyRanges = {};
      Object.keys(newRanges).forEach(day => {
        deepCopyRanges[day] = newRanges[day].map(range => ({
          start: range.start,
          end: range.end
        }));
      });
      setInitialRanges(deepCopyRanges);
      
      // 현재 state에 적용 (깊은 복사)
      setMonRanges(newRanges['월'].map(r => ({ start: r.start, end: r.end })));
      setTueRanges(newRanges['화'].map(r => ({ start: r.start, end: r.end })));
      setWedRanges(newRanges['수'].map(r => ({ start: r.start, end: r.end })));
      setThuRanges(newRanges['목'].map(r => ({ start: r.start, end: r.end })));
      setFriRanges(newRanges['금'].map(r => ({ start: r.start, end: r.end })));
      setSatRanges(newRanges['토'].map(r => ({ start: r.start, end: r.end })));
      setSunRanges(newRanges['일'].map(r => ({ start: r.start, end: r.end })));
    }
  }, [isOpen, initialCoreTime]);

  // ranges -> coreTime 변환
  const passTime = () => {
    const toTime = (idx) => {
      const h = Math.floor(idx / 2);
      const m = idx % 2 === 0 ? '00' : '30';
      return `${h.toString().padStart(2, '0')}:${m}`;
    };
    
    const formatTimeLabel = (timeStr) => {
      const [hour, minute] = timeStr.split(':').map(Number);
      const period = hour < 12 ? '오전' : '오후';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${period} ${displayHour}:${minute.toString().padStart(2, '0')}`;
    };
    
    const timeList = [];
    const rangeData = [
      { ranges: monRanges, day: '월', weekday: 0 },
      { ranges: tueRanges, day: '화', weekday: 1 },
      { ranges: wedRanges, day: '수', weekday: 2 },
      { ranges: thuRanges, day: '목', weekday: 3 },
      { ranges: friRanges, day: '금', weekday: 4 },
      { ranges: satRanges, day: '토', weekday: 5 },
      { ranges: sunRanges, day: '일', weekday: 6 }
    ];
    
    rangeData.forEach(({ ranges, day, weekday }) => {
      if (ranges.length) {
        ranges.forEach(r => {
          const startTime = toTime(r.start);
          const endTime = toTime(r.end + 1);
          timeList.push({ 
            day, 
            weekday, 
            start: JSON.stringify({ 
              key: r.start, 
              label: formatTimeLabel(startTime), 
              value: startTime 
            }),
            end: JSON.stringify({ 
              key: r.end + 1, 
              label: formatTimeLabel(endTime), 
              value: endTime 
            })
          });
        });
      }
    });
    
    setCoreTime(timeList);
  };

  // 시간 구간 텍스트 변환 함수
  const rangesToText = (ranges) => {
    const toTime = (idx) => {
      const h = Math.floor(idx / 2);
      const m = idx % 2 === 0 ? '00' : '30';
      return `${h.toString().padStart(2, '0')}:${m}`;
    };
    if (!ranges.length) return '-';
    return ranges.map(r => `${toTime(r.start)}~${toTime(r.end + 1)}`).join(', ');
  };

  const closeWithReset = () => {
    closeDialog();
  };

  return (
    <Dialog open={isOpen} onClose={closeWithReset} fullWidth maxWidth="sm">
      <DialogContent sx={{ p: smUp ? 4 : 3 }}>
        <Typography variant="h5" gutterBottom>
          코어 타임을 설정해 주세요.
        </Typography>
        <Typography variant={smUp ? "body1" : "body2"} sx={{ color: smUp ? "text.primary" : "text.secondary", letterSpacing: smUp ? -0.2 : 0 }}>
          언제든 자유롭게 근무할 수 있지만, 쉽게 소통할 수 있는 집중 근무 시간을 설정해 주세요.
        </Typography>
        <Typography variant={smUp ? "body1" : "body2"} sx={{ color: smUp ? "text.primary" : "text.secondary" }}>
          이 시간에는 출근을 기록해야하며, 그렇지 않을시 불이익이 있을 수 있습니다.
        </Typography>
        <Typography variant={smUp ? "body1" : "body2"} sx={{ color: smUp ? "text.primary" : "text.secondary" }}>
          코어 타임은 한국 시간(KST) 기준으로 입력해주세요.
        </Typography>
        <Stack spacing={{ xs: 2, sm: 3 }}>
          <WeekdayTimeRangeSelector weekdayName="월" ranges={monRanges} onChange={setMonRanges} allRanges={allRanges} setAllRanges={setAllRanges} weekdayIndex={0} weekdayLabelList={weekdayLabelList} />
          <WeekdayTimeRangeSelector weekdayName="화" ranges={tueRanges} onChange={setTueRanges} allRanges={allRanges} setAllRanges={setAllRanges} weekdayIndex={1} weekdayLabelList={weekdayLabelList} />
          <WeekdayTimeRangeSelector weekdayName="수" ranges={wedRanges} onChange={setWedRanges} allRanges={allRanges} setAllRanges={setAllRanges} weekdayIndex={2} weekdayLabelList={weekdayLabelList} />
          <WeekdayTimeRangeSelector weekdayName="목" ranges={thuRanges} onChange={setThuRanges} allRanges={allRanges} setAllRanges={setAllRanges} weekdayIndex={3} weekdayLabelList={weekdayLabelList} />
          <WeekdayTimeRangeSelector weekdayName="금" ranges={friRanges} onChange={setFriRanges} allRanges={allRanges} setAllRanges={setAllRanges} weekdayIndex={4} weekdayLabelList={weekdayLabelList} />
          <WeekdayTimeRangeSelector weekdayName="토" ranges={satRanges} onChange={setSatRanges} allRanges={allRanges} setAllRanges={setAllRanges} weekdayIndex={5} weekdayLabelList={weekdayLabelList} />
          <WeekdayTimeRangeSelector weekdayName="일" ranges={sunRanges} onChange={setSunRanges} allRanges={allRanges} setAllRanges={setAllRanges} weekdayIndex={6} weekdayLabelList={weekdayLabelList} />
        </Stack>
        {/* 선택한 시간 구간 텍스트 */}
        <Box sx={{ mt: 4, bgcolor: 'grey.50', borderRadius: 1, p: 2 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">선택한 시간 (한국 시간 KST 기준)</Typography>
            <Typography variant="body2">월: {rangesToText(monRanges)}</Typography>
            <Typography variant="body2">화: {rangesToText(tueRanges)}</Typography>
            <Typography variant="body2">수: {rangesToText(wedRanges)}</Typography>
            <Typography variant="body2">목: {rangesToText(thuRanges)}</Typography>
            <Typography variant="body2">금: {rangesToText(friRanges)}</Typography>
            <Typography variant="body2">토: {rangesToText(satRanges)}</Typography>
            <Typography variant="body2">일: {rangesToText(sunRanges)}</Typography>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ mx: { xs: 0, sm: 1 }, mb: { xs: 0, sm: 1 } }}>
        <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={{ xs: 1, sm: 4 }} sx={{ width: 1, justifyContent: "flex-end" }}>
          <LoadingButton size="large" sx={{ px: 4 }} onClick={closeWithReset} loading={submittingCoreTime}>
            취소
          </LoadingButton>
          <LoadingButton 
            variant="contained" 
            size="large" 
            sx={{ px: 4 }} 
            onClick={passTime} 
            loading={submittingCoreTime}
            disabled={!hasChanges()}
          >
            {coreTimeUpdated ? '변경하기' : '등록하기'}
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
